import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { isFeatureOn, type Feature } from '../../lib/features';
import { featureGate } from '../../router/gates';
import { appLayoutRoute } from '../../router/parents';
import { ProjectLayout } from './pages/ProjectLayout';
import { ProjectsListPage } from './pages/ProjectsListPage';

/**
 * Projects routes (code-based TanStack Router). Heavy screens are lazy-loaded so the drawing
 * viewer (pdf.js) and Gantt are split out of the main bundle.
 */

/** Hidden project tabs (lib/features.ts) send you to the project overview. */
function projectFeatureGate(feature: Feature, projectId: string) {
  if (!isFeatureOn(feature)) throw redirect({ to: '/projects/$projectId', params: { projectId }, replace: true });
}

const optionalId = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);

const projectsIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/projects',
  component: ProjectsListPage,
});

const bidInvitationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/projects/bids/invitations',
  beforeLoad: featureGate('bids.invitations', '/projects'),
  validateSearch: (s: Record<string, unknown>): { package?: string } => ({ package: optionalId(s.package) }),
  component: lazyRouteComponent(() => import('./pages/BidsPages'), 'BidInvitationsPage'),
});

const messagesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/projects/messages',
  validateSearch: (s: Record<string, unknown>): { channel?: string } => ({ channel: optionalId(s.channel) }),
  component: lazyRouteComponent(() => import('./pages/MessagesPage'), 'MessagesPage'),
});

const timesheetsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/projects/timesheets',
  component: lazyRouteComponent(() => import('./pages/TimesheetsPage'), 'TimesheetsPage'),
});

export const projectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/projects/$projectId',
  component: ProjectLayout,
});

const overviewRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/OverviewPage'), 'OverviewPage'),
});

const scheduleRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/schedule',
  validateSearch: (s: Record<string, unknown>): { view?: 'gantt' | 'list' | 'lookahead'; task?: string } => ({ view: s.view === 'gantt' || s.view === 'list' || s.view === 'lookahead' ? (s.view as 'gantt' | 'list' | 'lookahead') : undefined, task: optionalId(s.task) }),
  component: lazyRouteComponent(() => import('./pages/SchedulePage'), 'SchedulePage'),
});

const teamRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/team',
  component: lazyRouteComponent(() => import('./pages/TeamPage'), 'TeamPage'),
});

const documentsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/documents',
  validateSearch: (s: Record<string, unknown>): { folder?: string; doc?: string } => ({ folder: optionalId(s.folder), doc: optionalId(s.doc) }),
  component: lazyRouteComponent(() => import('./pages/DocumentsPage'), 'DocumentsPage'),
});

const drawingsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/drawings',
  validateSearch: (s: Record<string, unknown>): { set?: string } => ({ set: optionalId(s.set) }),
  component: lazyRouteComponent(() => import('./pages/DrawingsPage'), 'DrawingsPage'),
});

const drawingViewerRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/drawings/$drawingId',
  validateSearch: (s: Record<string, unknown>): { revision?: string } => ({ revision: optionalId(s.revision) }),
  component: lazyRouteComponent(() => import('./pages/DrawingViewerPage'), 'DrawingViewerPage'),
});

const rfisRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/rfis',
  validateSearch: (s: Record<string, unknown>): { rfi?: string } => ({ rfi: optionalId(s.rfi) }),
  component: lazyRouteComponent(() => import('./pages/RfisPage'), 'RfisPage'),
});

const submittalsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/submittals',
  validateSearch: (s: Record<string, unknown>): { submittal?: string } => ({ submittal: optionalId(s.submittal) }),
  component: lazyRouteComponent(() => import('./pages/SubmittalsPage'), 'SubmittalsPage'),
});

const dailyLogsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/daily-logs',
  validateSearch: (s: Record<string, unknown>): { log?: string } => ({ log: optionalId(s.log) }),
  component: lazyRouteComponent(() => import('./pages/DailyLogsPage'), 'DailyLogsPage'),
});

const photosRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/photos',
  validateSearch: (s: Record<string, unknown>): { album?: string } => ({ album: optionalId(s.album) }),
  component: lazyRouteComponent(() => import('./pages/PhotosPage'), 'PhotosPage'),
});

const punchRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/punch',
  validateSearch: (s: Record<string, unknown>): { item?: string } => ({ item: optionalId(s.item) }),
  component: lazyRouteComponent(() => import('./pages/PunchPage'), 'PunchPage'),
});

const timeRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/time',
  component: lazyRouteComponent(() => import('./pages/ProjectTimePage'), 'ProjectTimePage'),
});

const meetingsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/meetings',
  beforeLoad: ({ params }) => projectFeatureGate('project.meetings', params.projectId),
  validateSearch: (s: Record<string, unknown>): { meeting?: string } => ({ meeting: optionalId(s.meeting) }),
  component: lazyRouteComponent(() => import('./pages/MeetingsPage'), 'MeetingsPage'),
});

const bidsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/bids',
  beforeLoad: ({ params }) => projectFeatureGate('project.bids', params.projectId),
  component: lazyRouteComponent(() => import('./pages/BidsPages'), 'BidPackagesPage'),
});

const bidPackageRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/bids/$packageId',
  beforeLoad: ({ params }) => projectFeatureGate('project.bids', params.projectId),
  component: lazyRouteComponent(() => import('./pages/BidsPages'), 'BidPackagePage'),
});

const chatRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/chat',
  validateSearch: (s: Record<string, unknown>): { channel?: string } => ({ channel: optionalId(s.channel) }),
  component: lazyRouteComponent(() => import('./pages/ProjectChatPage'), 'ProjectChatPage'),
});

export const projectsRoutes = [
  projectsIndexRoute,
  bidInvitationsRoute,
  messagesRoute,
  timesheetsRoute,
  projectRoute.addChildren([
    overviewRoute,
    scheduleRoute,
    teamRoute,
    documentsRoute,
    drawingsRoute,
    drawingViewerRoute,
    rfisRoute,
    submittalsRoute,
    dailyLogsRoute,
    photosRoute,
    punchRoute,
    timeRoute,
    meetingsRoute,
    bidsRoute,
    bidPackageRoute,
    chatRoute,
  ]),
] as const;
